import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion, Variants } from "framer-motion";
import React, { useCallback } from "react";
import AboutCompany from "../../MicrospecDynamicUI/Pages/SectionsMapper/About/AboutCompany";
import { AboutCompanySpec } from "../../MicrospecDynamicUI/Pages/SectionsMapper/About/AboutCompany/types";
import HeroWithStatsBar from "../../MicrospecDynamicUI/Pages/SectionsMapper/Hero/HeroWithStatsBar";
import { HeroWithStatsBarSpec } from "../../MicrospecDynamicUI/Pages/SectionsMapper/Hero/HeroWithStatsBar/types";
import ClientTestimonials from "../../MicrospecDynamicUI/Pages/SectionsMapper/Testimonials/ClientTestimonials";
import { ClientTestimonialsSpec } from "../../MicrospecDynamicUI/Pages/SectionsMapper/Testimonials/ClientTestimonials/types";
import { useMicrospecThemeLanguageContext } from "../../MicrospecDynamicUI/Themes";
import CommonFAQ from "../Components/CommonFAQ";
import FirebaseTitledCardsWithCTAButtonSection from "../Components/FirebaseTitledCardsWithCTAButtonSection";
import { CATEGORIES_COLLECTION, PROJECTS_COLLECTION } from "../constants/collectionPaths";

// ─── Static specs ────────────────────────────────────────────────────────────

const heroSpec: HeroWithStatsBarSpec = {
    id: 'hero',
    // backgroundImageSource: '/static/images/timber-hero.png',
    backgroundVideoSource: '/static/videos/microspec_application_timber_hero.mp4',
    backgroundFilter: { type: 'darken', intensity: 0.45 },
    title: { localizationObject: { type: 'Literal', text: 'גולן סחר עץ' } },
    subtitle: { localizationObject: { type: 'Literal', text: 'עצים איכותיים לכל פרויקט — מהיצרן ישירות אליך' } },
    actionButtonText: { localizationObject: { type: 'Literal', text: 'בקש הצעת מחיר' } },
    actionButtonHref: '/contact',
    secondaryActionButtonText: { localizationObject: { type: 'Literal', text: 'לקטלוג המוצרים' } },
    secondaryActionButtonHref: '/categories',
    secondaryActionButtonBackground: '#DF643B',
    stats: [
        { value: { localizationObject: { type: 'Literal', text: '8+' } }, label: { localizationObject: { type: 'Literal', text: 'שנות ניסיון' } } },
        { value: { localizationObject: { type: 'Literal', text: '50+' } }, label: { localizationObject: { type: 'Literal', text: 'סוגי עץ' } } },
        { value: { localizationObject: { type: 'Literal', text: '7,500+' } }, label: { localizationObject: { type: 'Literal', text: 'לקוחות מרוצים' } } },
        { value: { localizationObject: { type: 'Literal', text: '100%' } }, label: { localizationObject: { type: 'Literal', text: 'עץ מוסמך' } } },
    ],
    showScrollIndicator: true,
};

const aboutSpec: AboutCompanySpec = {
    id: 'about',
    backgroundColor: '#f8f6f2',
    accentColor: '#2E673E',
    sectionTitle: { localizationObject: { type: 'Literal', text: 'למה לבחור בגולן סחר עץ?' } },
    companyName: { localizationObject: { type: 'Literal', text: 'גולן סחר עץ' } },
    description: {
        localizationObject: {
            type: 'Literal',
            text: 'מעל 8 שנים אנו מספקים עצים ומוצרי עץ מהשורה הראשונה לקבלנים, נגרים, אדריכלים ולקוחות פרטיים ברחבי הארץ. אנו מחברים אתכם ישירות למקור — ללא מתווכים — ומבטיחים מחירים תחרותיים, איכות גבוהה ושירות אישי לכל לקוח.',
        },
    },
    foundedYear: '2016',
    companyImage: '/static/images/timber-hero.png',
    titleColor: '#1a3d24',
    textColor: '#4a5568',
    valuesCardsTitleColor: '#1a3d24',
    valuesCardsTextColor: '#4a5568',
    valuesCardsBackground: 'rgba(255,255,255,0.85)',
    values: [
        {
            icon: '🌲',
            title: 'עץ מוסמך ואיכותי',
            description: 'כל מוצר עובר בדיקת איכות קפדנית ומגיע עם תעודת מקור ותקנים בינלאומיים.',
        },
        {
            icon: '🚚',
            title: 'אספקה מהירה לכל הארץ',
            description: 'משלוח ישיר לאתר הבנייה, לסדנה או לבית — בתיאום מראש ובזמן שנקבע.',
        },
        {
            icon: '💬',
            title: 'ייעוץ מקצועי אישי',
            description: 'צוות המומחים שלנו זמין לסייע בבחירת הסוג, הממדים והגמר המתאים לכל פרויקט.',
        },
        {
            icon: '💰',
            title: 'מחירים תחרותיים',
            description: 'ישירות מהמקור ללא מתווכים — חסכו עד 30% לעומת המחיר הקמעוני.',
        },
    ],
};

const testimonialsSpec: ClientTestimonialsSpec = {
    id: 'testimonials',
    backgroundColor: '#1a3d24',
    titleColor: '#ffffff',
    subtitleColor: 'rgba(255,255,255,0.75)',
    accentColor: '#DF643B',
    title: 'מה הלקוחות שלנו אומרים',
    subtitle: 'קבלנים, נגרים ואדריכלים שבחרו בגולן סחר עץ',
    actionButtonText: 'ליצירת קשר',
    actionButtonHref: '/contact',
    actionButtonBackground: '#DF643B',
    actionButtonTextColor: '#ffffff',
    autoRotate: true,
    rotationInterval: 5000,
    testimonials: [
        {
            id: 1,
            name: 'משה לוי',
            location: 'הרצליה',
            rating: 5,
            comment: 'הזמנתי קורות בנייה לשלושה פרויקטים ברציפות. האיכות מעולה, המחירים הוגנים והספקה בזמן — בדיוק מה שצריך כשאתה קבלן עם לוחות זמנים.',
            avatar: null,
            verified: true,
        },
        {
            id: 2,
            name: 'מיכל כהן',
            location: 'תל אביב',
            rating: 5,
            comment: 'כמעצבת פנים אני מחפשת עצים נדירים ואיכותיים לפרויקטי יוקרה. גולן סחר עץ הוא הכתובת שלי — מגוון עצום, ייעוץ מקצועי ושירות מעל ומעבר.',
            avatar: null,
            verified: true,
        },
        {
            id: 3,
            name: 'אבי שפירא',
            location: 'ירושלים',
            rating: 5,
            comment: 'נגר מקצועי עם 20 שנות ניסיון. עובד עם גולן סחר עץ כבר 5 שנים. העץ תמיד יבש, נקי ובדיוק לפי ההזמנה. ספק שאפשר לסמוך עליו.',
            avatar: null,
            verified: true,
        },
        {
            id: 4,
            name: 'רונית אברהם',
            location: 'חיפה',
            rating: 5,
            comment: 'הזמנתי עץ לדק גינה פרטי. התהליך היה פשוט, הם עזרו לי לבחור את הסוג המתאים לאקלים הצפוני, וקיבלתי בדיוק מה שרציתי.',
            avatar: null,
            verified: true,
        },
    ],
};

// ─── Process steps data ───────────────────────────────────────────────────────

const PROCESS_STEPS = [
    {
        number: '01',
        icon: '🌲',
        title: 'בחר מוצר',
        description: 'עיין בקטלוג העצים שלנו ובחר את הסוג, המידה והכמות המתאימים לפרויקט שלך.',
    },
    {
        number: '02',
        icon: '📋',
        title: 'קבל הצעת מחיר',
        description: 'צרו קשר בוואטסאפ, בטלפון או בטופס — ונחזור אליכם עם הצעה מותאמת אישית תוך יום עסקים.',
    },
    {
        number: '03',
        icon: '🚚',
        title: 'קבל את ההזמנה',
        description: 'נארז בקפידה ונספק ישירות לאתר הבנייה, לסדנה או לבית — בכל רחבי הארץ.',
    },
];

// ─── Animated sub-components ─────────────────────────────────────────────────

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const staggerContainer: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18 } },
};

function ProcessSection() {
    const PRIMARY = '#2E673E';

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(160deg, ${alpha(PRIMARY, 0.06)} 0%, #fff 60%)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* decorative circle */}
            <Box sx={{
                position: 'absolute', top: -80, right: -80, width: 320, height: 320,
                borderRadius: '50%', background: alpha(PRIMARY, 0.05), pointerEvents: 'none',
            }} />

            <Container maxWidth="lg">
                <motion.div
                    initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUp}
                >
                    <Typography
                        variant="overline"
                        sx={{ display: 'block', textAlign: 'center', color: PRIMARY, fontWeight: 700, letterSpacing: 3, mb: 1 }}
                    >
                        התהליך שלנו
                    </Typography>
                    <Typography
                        variant="h3"
                        sx={{ textAlign: 'center', fontWeight: 800, color: '#1a3d24', mb: 1.5 }}
                    >
                        איך זה עובד?
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 520, mx: 'auto', mb: 8 }}
                    >
                        שלושה צעדים פשוטים — מבחירת המוצר ועד קבלת ההזמנה לפתח הבית
                    </Typography>
                </motion.div>

                <motion.div
                    initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
                    variants={staggerContainer}
                >
                    <Grid container spacing={4} justifyContent="center">
                        {PROCESS_STEPS.map((step, idx) => (
                            <Grid item xs={12} md={4} key={step.number}>
                                <motion.div variants={fadeUp} style={{ height: '100%' }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            p: { xs: 3.5, md: 4.5 },
                                            border: `1.5px solid ${alpha(PRIMARY, 0.15)}`,
                                            borderRadius: 4,
                                            textAlign: 'center',
                                            background: '#fff',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transition: 'all 0.35s ease',
                                            '&:hover': {
                                                borderColor: PRIMARY,
                                                boxShadow: `0 12px 40px ${alpha(PRIMARY, 0.14)}`,
                                                transform: 'translateY(-6px)',
                                            },
                                        }}
                                    >
                                        {/* step number badge */}
                                        <Box sx={{
                                            position: 'absolute', top: 20, right: 20,
                                            width: 40, height: 40,
                                            borderRadius: '50%',
                                            background: alpha(PRIMARY, 0.1),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: PRIMARY }}>
                                                {step.number}
                                            </Typography>
                                        </Box>

                                        {/* icon */}
                                        <Box sx={{
                                            width: 80, height: 80, mx: 'auto', mb: 3,
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.12)}, ${alpha(PRIMARY, 0.04)})`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 36,
                                        }}>
                                            {step.icon}
                                        </Box>

                                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a3d24', mb: 1.5 }}>
                                            {step.title}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            {step.description}
                                        </Typography>

                                        {/* connector arrow (not on last) */}
                                        {idx < PROCESS_STEPS.length - 1 && (
                                            <Box sx={{
                                                display: { xs: 'none', md: 'block' },
                                                position: 'absolute',
                                                top: '50%',
                                                left: -24,
                                                transform: 'translateY(-50%)',
                                                fontSize: 28,
                                                color: alpha(PRIMARY, 0.35),
                                                userSelect: 'none',
                                            }}>
                                                ←
                                            </Box>
                                        )}
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const MainPage: React.FC = () => {
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    const mapCategoryToCard = useCallback((doc: any) => ({
        id: doc.id,
        image: doc.image,
        icon: doc.icon,
        title: localizationProvider.translate(doc.name),
        href: `/category/${doc.id}/products`,
    }), [localizationProvider]);

    const mapProjectToCard = useCallback((doc: any) => {
        const firstMedia: string | undefined =
            Array.isArray(doc.mediaUrls) && doc.mediaUrls.length > 0
                ? doc.mediaUrls[0]
                : doc.image;
        return {
            id: doc.id,
            mediaUrl: firstMedia,
            icon: doc.icon,
            title: localizationProvider.translate(doc.name),
            href: `/projects/${doc.id}`,
        };
    }, [localizationProvider]);

    return (
        <Box>
            {/* 1 ▸ Hero */}
            <HeroWithStatsBar spec={heroSpec} />

            {/* 2 ▸ Why Choose Us */}
            <AboutCompany spec={aboutSpec} />

            {/* 3 ▸ Product Categories */}
            <FirebaseTitledCardsWithCTAButtonSection spec={{
                collectionPath: CATEGORIES_COLLECTION,
                mapDocToCard: mapCategoryToCard,
                id: 'categories',
                sectionTitle: { localizationObject: { type: 'Literal', text: 'קטגוריות מוצרים' } },
                limit: 8,
                actionButtonText: { localizationObject: { type: 'Literal', text: 'לכל הקטגוריות' } },
                actionButtonHref: '/categories',
                orderField: 'order',
                orderDirection: 'asc',
            }} />

            {/* 4 ▸ How it Works */}
            <ProcessSection />

            {/* 5 ▸ Projects Gallery */}
            <FirebaseTitledCardsWithCTAButtonSection spec={{
                collectionPath: PROJECTS_COLLECTION,
                mapDocToCard: mapProjectToCard,
                id: 'projects',
                sectionTitle: { localizationObject: { type: 'Literal', text: 'גלריית פרויקטים' } },
                limit: 4,
                actionButtonText: { localizationObject: { type: 'Literal', text: 'לכל הפרויקטים' } },
                actionButtonHref: '/projects',
                orderField: 'order',
                orderDirection: 'asc',
            }} />

            {/* 6 ▸ Testimonials */}
            <ClientTestimonials spec={testimonialsSpec} />

            {/* 8 ▸ FAQ */}
            <CommonFAQ />

            {/* 7 ▸ WhatsApp CTA */}
            {/* <WhatsAppCTABanner /> */}
        </Box>
    );
};

export default MainPage;